import React, { type ComponentType, Suspense } from "react";
import { PageLoading } from "./lazyLoad/components.tsx";
import { LazyErrorBoundary } from "./lazyLoad/errorBoundary.tsx";
import { createRetryImport } from "./lazyLoad/retry.ts";
import type { LazyImportFunction, LazyLoadOptions } from "./lazyLoad/types.ts";
import { performanceMonitor } from "./performanceMonitor";

/**
 * @description Lazy loading higher-order component
 * @param importFunc - Dynamic import function
 * @param options - Configuration options
 * @returns Lazy loaded component
 */
export function lazyLoad<T extends Record<string, unknown> = Record<string, unknown>>(
  importFunc: LazyImportFunction<T>,
  options: LazyLoadOptions = {},
): ComponentType<T> {
  const {
    fallback: Fallback = PageLoading,
    componentName,
    enableMonitoring = true,
    retryAttempts = 3,
    retryDelay = 1000,
  } = options;

  // Create retry-enabled import function if retry is configured
  const enhancedImportFunc =
    retryAttempts > 1
      ? createRetryImport(importFunc, {
          attempts: retryAttempts,
          delay: retryDelay,
        })
      : importFunc;

  // Create lazy component
  const LazyComponent = React.lazy(async () => {
    if (componentName && enableMonitoring) {
      try {
        performanceMonitor.startLoading(componentName);
      } catch (monitorError) {
        console.warn("Performance monitoring failed:", monitorError);
      }
    }

    try {
      const module = await enhancedImportFunc();
      if (componentName && enableMonitoring) {
        try {
          performanceMonitor.endLoading(componentName, true);
        } catch (monitorError) {
          console.warn("Performance monitoring failed:", monitorError);
        }
      }
      return module;
    } catch (error) {
      if (componentName && enableMonitoring) {
        try {
          performanceMonitor.endLoading(
            componentName,
            false,
            error instanceof Error ? error.message : String(error),
          );
        } catch (monitorError) {
          console.warn("Performance monitoring failed:", monitorError);
        }
      }
      // Let the retry mechanism handle the error instead of throwing immediately
      throw error;
    }
  });

  // Return wrapped component
  const WrappedComponent = (props: T) => {
    return (
      <LazyErrorBoundary>
        <Suspense fallback={<Fallback />}>
          <LazyComponent {...props} />
        </Suspense>
      </LazyErrorBoundary>
    );
  };

  // Set display name for debugging
  WrappedComponent.displayName = `LazyLoad(${componentName || "Component"})`;

  return WrappedComponent as ComponentType<T>;
}

/**
 * @description Simplified lazy loading function with default loading component
 * @param importFunc - Dynamic import function
 * @returns Lazy loaded component
 */
export const lazy = <T extends Record<string, unknown> = Record<string, unknown>>(
  importFunc: LazyImportFunction<T>,
): ComponentType<T> => lazyLoad(importFunc);

/**
 * @description Preload function to load components in advance
 * @param importFunc - Dynamic import function
 */
export const preload = <T extends Record<string, unknown> = Record<string, unknown>>(
  importFunc: LazyImportFunction<T>,
): void => {
  // Preload but don't wait for result
  importFunc().catch(() => {
    // Silently handle preload errors
  });
};

export default lazyLoad;
